import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { haptic } from '../lib/telegram';

/**
 * Settings, kept short: one row per thing («Инфо для клиентов», «Навыки ·
 * 6», «Метки · 3»), and the editor in a sheet that opens from it.
 */

/**
 * One compact row: icon, title, a status line, and a chevron. A short
 * current value can sit on the right instead of on a second line, and a
 * dot marks a row whose screen still has something unset.
 */
export function SetRow({ icon, title, status, onOpen, id, value, dot }: {
  icon: string; title: string; status?: ReactNode; onOpen: () => void; id?: string;
  value?: ReactNode; dot?: boolean;
}) {
  return (
    <button className="setrow" id={id} onClick={() => { haptic.tap(); onOpen(); }}>
      <span className="setrow__icon" aria-hidden="true">{icon}</span>
      <span className="setrow__main">
        <b>{title}{dot && <i className="setrow__dot" aria-hidden="true" />}</b>
        {status && <small>{status}</small>}
      </span>
      {value && <span className="setrow__value">{value}</span>}
      <span className="setrow__chev" aria-hidden="true">›</span>
    </button>
  );
}

/**
 * A sheet whose actions stay pinned at the bottom while the fields above
 * scroll. Text fields inside keep the cursor above this bar and the
 * keyboard (GrowArea looks for .psheet__foot).
 */
export function PinnedSheet({ title, onClose, footer, children, full, lock, action }: {
  title: ReactNode; onClose: () => void; footer: ReactNode; children: ReactNode;
  /** A button beside the close button («Изменить» on the drill card). */
  action?: ReactNode;
  /** The whole screen, scrolling inside itself; the page behind is locked. */
  full?: boolean;
  /** The page behind is locked while the sheet is open. */
  lock?: boolean;
}) {
  useEffect(() => (full || lock ? lockPage() : undefined), [full, lock]);
  return createPortal(
    <div className={`sheet${full ? ' sheet--full' : ''}`} onClick={onClose}>
      <div className={`sheet__panel psheet${full ? ' psheet--full' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="psheet__head">
          <div className="sheet__title">{title}</div>
          {action}
          <button className="psheet__x" aria-label="×" onClick={onClose}>×</button>
        </div>
        <div className="psheet__body">{children}</div>
        <div className="psheet__foot">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The page behind a sheet held where it is: the body fixed at its scroll
 * position, so neither a swipe in the sheet nor the keyboard moves it, and
 * put back on close. Counted, so one sheet replacing another keeps it held.
 */
let locks = 0;
let heldAt = 0;
export function lockPage(): () => void {
  if (locks++ === 0) {
    heldAt = window.scrollY;
    Object.assign(document.body.style, { position: 'fixed', top: `-${heldAt}px`, left: '0', right: '0', width: '100%', overflow: 'hidden' });
  }
  return () => {
    if (--locks > 0) return;
    Object.assign(document.body.style, { position: '', top: '', left: '', right: '', width: '', overflow: '' });
    window.scrollTo(0, heldAt);
  };
}
